<template>
  <router-link
    :to="`/support/tickets/${ticket.id}`"
    class="block hover:bg-gray-50 transition-colors border-l-4"
    :class="waitingTimeClass"
  >
    <div class="px-4 py-4 sm:px-6">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3 flex-1 min-w-0">
          <!-- Gravatar -->
          <img
            :src="getGravatarUrl(ticket.email)"
            :alt="ticket.name"
            class="w-10 h-10 rounded-full flex-shrink-0 ring-2 ring-gray-200"
          />

          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 flex-wrap mb-1">
              <p class="text-sm font-medium text-gray-900">
                #{{ ticket.tid }} - {{ ticket.subject }}
              </p>
              <TicketStatusBadge :status="ticket.status" />
              <TicketPriorityBadge :priority="ticket.priority" />
            </div>
            <div class="mt-2 flex items-center text-sm text-gray-500">
              <span>{{ ticket.name }} ({{ ticket.email }})</span>
              <span class="mx-2">•</span>
              <span>{{ ticket.department }}</span>
            </div>
          </div>
        </div>
        <div class="flex flex-col items-end ml-5">
          <!-- Temps d'attente mis en avant -->
          <div class="flex items-center space-x-2 mb-2">
            <svg class="h-5 w-5" :class="waitingTimeIconClass" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd" />
            </svg>
            <span class="text-sm font-semibold" :class="waitingTimeTextClass">
              {{ getWaitingTime() }}
            </span>
          </div>
          <p class="text-xs text-gray-400">
            Créé: {{ formatDate(ticket.date) }}
          </p>
        </div>
      </div>
    </div>
  </router-link>
</template>

<script>
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import md5 from 'md5';
import TicketStatusBadge from './TicketStatusBadge.vue';
import TicketPriorityBadge from './TicketPriorityBadge.vue';

export default {
  name: 'TicketCard',
  components: {
    TicketStatusBadge,
    TicketPriorityBadge
  },
  props: {
    ticket: {
      type: Object,
      required: true
    }
  },
  computed: {
    waitingHours() {
      if (!this.ticket.lastreply) return 0;
      try {
        const lastReplyDate = parseISO(this.ticket.lastreply.replace(' ', 'T'));
        const now = new Date();
        const diffMs = now - lastReplyDate;
        return diffMs / (1000 * 60 * 60); // Convertir en heures
      } catch (e) {
        return 0;
      }
    },
    waitingTimeClass() {
      // Bordure colorée selon l'urgence
      if (this.waitingHours > 48) return 'border-red-500'; // > 2 jours = rouge
      if (this.waitingHours > 24) return 'border-orange-500'; // > 1 jour = orange
      if (this.waitingHours > 12) return 'border-yellow-500'; // > 12h = jaune
      return 'border-green-500'; // < 12h = vert
    },
    waitingTimeIconClass() {
      if (this.waitingHours > 48) return 'text-red-500';
      if (this.waitingHours > 24) return 'text-orange-500';
      if (this.waitingHours > 12) return 'text-yellow-500';
      return 'text-green-500';
    },
    waitingTimeTextClass() {
      if (this.waitingHours > 48) return 'text-red-700';
      if (this.waitingHours > 24) return 'text-orange-700';
      if (this.waitingHours > 12) return 'text-yellow-700';
      return 'text-green-700';
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        // Format: 2025-01-15 10:30:00
        const date = parseISO(dateString.replace(' ', 'T'));
        return format(date, 'dd MMM yyyy HH:mm', { locale: fr });
      } catch (e) {
        return dateString;
      }
    },
    getWaitingTime() {
      if (!this.ticket.lastreply) return 'Nouveau';
      try {
        const lastReplyDate = parseISO(this.ticket.lastreply.replace(' ', 'T'));
        return formatDistanceToNow(lastReplyDate, {
          addSuffix: true,
          locale: fr
        });
      } catch (e) {
        return 'N/A';
      }
    },
    getGravatarUrl(email) {
      if (!email) return 'https://www.gravatar.com/avatar/?d=mp';

      // Normaliser l'email
      const normalizedEmail = email.trim().toLowerCase();

      // Créer le hash MD5
      const hash = md5(normalizedEmail);

      // Retourner l'URL Gravatar
      return `https://www.gravatar.com/avatar/${hash}?d=mp&s=80`;
    }
  }
};
</script>
